// StoreIssueNote.jsx
// ✅ New standardized design
// ✅ Existing search API preserved
// ✅ Bulk delete preserved
// ✅ Select all / single select preserved
// ✅ Edit sessionStorage flow preserved
// ✅ Add New session reset preserved
// ✅ Approval status preserved
// ✅ Dark mode + skin preserved

import React, {
  useContext,
  useMemo,
  useState,
} from "react";

import axios from "axios";
import { useNavigate } from "react-router-dom";

import LayoutWrapper from "../../../components/LayoutWrapper";
import HeaderBar from "../../../components/HeaderBar";

import {
  FaPlus,
  FaSearch,
  FaTrash,
  FaFilter,
  FaTimes,
  FaEdit,
  FaArrowLeft,
  FaClipboardList,
  FaCog,
  FaInfoCircle,
  FaCheckCircle,
  FaClock,
  FaListAlt,
} from "react-icons/fa";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import { ThemeContext } from "../../../context/ThemeContext";
import getCurrentSkin from "../../../utils/getCurrentSkin";

/* =========================================================
   API
========================================================= */

const API =
  "https://nissi-india.com/api/storeIssueNote";

/* =========================================================
   HELPERS
========================================================= */

const getTodayDate = () =>
  new Date()
    .toISOString()
    .split("T")[0];

const getSevenDaysAgo = () => {
  const date = new Date();

  date.setDate(
    date.getDate() - 7
  );

  return date
    .toISOString()
    .split("T")[0];
};

const formatDisplayDate = (
  value
) => {
  if (!value) {
    return "-";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return value;
  }

  return date.toLocaleDateString(
    "en-GB"
  );
};

const formatAmount = (
  value
) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "-";
  }

  const number =
    Number(value);

  if (
    !Number.isFinite(
      number
    )
  ) {
    return "-";
  }

  return number.toLocaleString(
    "en-IN",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );
};

/* =========================================================
   COMPONENT
========================================================= */

const StoreIssueNote =
  () => {
    const navigate =
      useNavigate();

    /* =======================================================
       THEME
    ======================================================= */

    const {
      isDark,
      skin,
      setSkin,
      theme,
    } = useContext(
      ThemeContext
    );

    const currentSkin =
      useMemo(
        () =>
          getCurrentSkin(
            theme || skin
          ),
        [
          theme,
          skin,
        ]
      );

    const baseColor =
      currentSkin?.baseColor ||
      "blue";

    const baseShade =
      currentSkin?.baseShade ||
      "600";

    /* =======================================================
       LOCAL STORAGE
    ======================================================= */

    const finYearId =
      localStorage.getItem(
        "finYearId"
      ) || "";

    const finYear =
      localStorage.getItem(
        "finYear"
      ) || "";

    const branchId =
      parseInt(
        localStorage.getItem(
          "branchId"
        ) ||
          localStorage.getItem(
            "branchid"
          ) ||
          "0",
        10
      );

    const branch =
      localStorage.getItem(
        "branchType"
      ) ||
      localStorage.getItem(
        "branchtype"
      ) ||
      "";

    const userId =
      localStorage.getItem(
        "userId"
      ) ||
      localStorage.getItem(
        "loginuserid"
      ) ||
      "";

    /* =======================================================
       HEADERS
    ======================================================= */

    const commonHeaders =
      useMemo(
        () => ({
          "x-finyearid":
            finYearId,

          "x-finyear":
            finYear,

          "x-branchid":
            branchId,

          "x-branch":
            branch,

          "x-userid":
            userId,
        }),
        [
          finYearId,
          finYear,
          branchId,
          branch,
          userId,
        ]
      );

    /* =======================================================
       DESIGN CLASSES
    ======================================================= */

    const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }`;

    const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }`;

    const cardClass = `rounded-2xl shadow-xl border overflow-hidden ${
      isDark
        ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
        : `bg-white ${
            currentSkin?.glass ||
            ""
          } border-gray-200`
    }`;

    const neutralButtonClass = `inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition ${
      isDark
        ? "border-slate-600 text-slate-300 hover:bg-slate-800"
        : "border-slate-300 text-slate-700 hover:bg-slate-100"
    }`;

    const tableRowClass = `transition-colors ${
      isDark
        ? "bg-gray-800/20 hover:bg-gray-700/30"
        : "bg-white hover:bg-gray-50"
    }`;

    const tableCellClass = `px-4 py-3 text-sm whitespace-nowrap ${
      isDark
        ? "text-gray-300"
        : "text-gray-900"
    }`;

    /* =======================================================
       STATE
    ======================================================= */

    const [
      showFilters,
      setShowFilters,
    ] = useState(true);

    const [
      fromDate,
      setFromDate,
    ] = useState(
      getSevenDaysAgo
    );

    const [
      toDate,
      setToDate,
    ] = useState(
      getTodayDate
    );

    const [
      scrNo,
      setScrNo,
    ] = useState("");

    const [
      data,
      setData,
    ] = useState([]);

    const [
      selectedItems,
      setSelectedItems,
    ] = useState([]);

    const [
      loading,
      setLoading,
    ] = useState(false);

    const [
      deleting,
      setDeleting,
    ] = useState(false);

    /* =======================================================
       PAGINATION
    ======================================================= */

    const [
      currentPage,
      setCurrentPage,
    ] = useState(1);

    const [
      pageSize,
      setPageSize,
    ] = useState(10);

    const totalPages =
      Math.max(
        1,
        Math.ceil(
          data.length /
            pageSize
        )
      );

    const indexOfLastItem =
      currentPage *
      pageSize;

    const indexOfFirstItem =
      indexOfLastItem -
      pageSize;

    const currentItems =
      useMemo(
        () =>
          data.slice(
            indexOfFirstItem,
            indexOfLastItem
          ),
        [
          data,
          indexOfFirstItem,
          indexOfLastItem,
        ]
      );

    /* =======================================================
       SEARCH
    ======================================================= */

    const handleSearch =
      async () => {
        if (
          !branchId
        ) {
          toast.warning(
            "Branch ID not found in localStorage"
          );

          return;
        }

        if (
          !fromDate ||
          !toDate
        ) {
          toast.warning(
            "Please select From Date and To Date"
          );

          return;
        }

        if (
          new Date(
            fromDate
          ) >
          new Date(
            toDate
          )
        ) {
          toast.warning(
            "From Date must be less than or equal to To Date"
          );

          return;
        }

        const issueNo =
          scrNo.trim() ===
          ""
            ? "%"
            : scrNo.trim();

        setLoading(
          true
        );

        try {
          const response =
            await axios.get(
              `${API}/storeIssue_grid`,
              {
                params: {
                  branchid:
                    branchId,

                  dtfrom:
                    fromDate,

                  dtto:
                    toDate,

                  storeIssueNo:
                    issueNo,
                },

                headers:
                  commonHeaders,
              }
            );

          const rows =
            Array.isArray(
              response.data
                ?.data
            )
              ? response.data
                  .data
              : [];

          setData(
            rows
          );

          setSelectedItems(
            []
          );

          setCurrentPage(
            1
          );

          if (
            rows.length ===
            0
          ) {
            toast.info(
              "No records found"
            );
          } else {
            toast.success(
              `Found ${rows.length} record(s)`
            );
          }
        } catch (error) {
          console.error(
            "Search error:",
            error
          );

          toast.error(
            error?.response
              ?.data
              ?.message ||
              "Failed to fetch listing"
          );

          setData(
            []
          );

          setSelectedItems(
            []
          );
        } finally {
          setLoading(
            false
          );
        }
      };

    /* =======================================================
       SELECT ALL
    ======================================================= */

    const allCurrentSelected =
      currentItems.length >
        0 &&
      currentItems.every(
        (item) =>
          selectedItems.includes(
            item.fld_storesissueid
          )
      );

    const toggleAll =
      (event) => {
        const checked =
          event.target.checked;

        if (checked) {
          setSelectedItems(
            (
              previous
            ) => {
              const ids =
                currentItems.map(
                  (item) =>
                    item.fld_storesissueid
                );

              return Array.from(
                new Set([
                  ...previous,
                  ...ids,
                ])
              );
            }
          );

          return;
        }

        const currentIds =
          currentItems.map(
            (item) =>
              item.fld_storesissueid
          );

        setSelectedItems(
          (
            previous
          ) =>
            previous.filter(
              (id) =>
                !currentIds.includes(
                  id
                )
            )
        );
      };

    /* =======================================================
       SELECT ONE
    ======================================================= */

    const toggleOne =
      (id) => {
        setSelectedItems(
          (
            previous
          ) =>
            previous.includes(
              id
            )
              ? previous.filter(
                  (
                    selectedId
                  ) =>
                    selectedId !==
                    id
                )
              : [
                  ...previous,
                  id,
                ]
        );
      };

    /* =======================================================
       DELETE
    ======================================================= */

    const handleBulkDelete =
      async () => {
        if (
          selectedItems.length ===
          0
        ) {
          toast.warning(
            "No items selected"
          );

          return;
        }

        const confirmed =
          window.confirm(
            `Delete ${selectedItems.length} selected record(s)?`
          );

        if (!confirmed) {
          return;
        }

        setDeleting(
          true
        );

        try {
          await axios.delete(
            `${API}/storesissue_delete`,
            {
              data: {
                ids:
                  selectedItems.map(
                    Number
                  ),
              },

              headers:
                commonHeaders,
            }
          );

          toast.success(
            "Deleted successfully"
          );

          setData(
            (
              previous
            ) =>
              previous.filter(
                (item) =>
                  !selectedItems.includes(
                    item.fld_storesissueid
                  )
              )
          );

          setSelectedItems(
            []
          );

          setCurrentPage(
            1
          );
        } catch (error) {
          console.error(
            "Delete error:",
            error
          );

          toast.error(
            error?.response
              ?.data
              ?.message ||
              "Failed to delete records"
          );
        } finally {
          setDeleting(
            false
          );
        }
      };

    /* =======================================================
       EDIT
    ======================================================= */

    const handleEdit =
      (item) => {
        const issueNo =
          item.fld_storesissueno ||
          "";

        const issueId =
          item.fld_storesissueid ||
          "";

        const createdUserId =
          item.fld_userid ||
          "";

        sessionStorage.setItem(
          "storeid",
          issueNo
        );

        sessionStorage.setItem(
          "storeuserid",
          String(
            createdUserId
          )
        );

        sessionStorage.setItem(
          "storeissueid",
          String(
            issueId
          )
        );

        navigate(
          "/inventory/StoreIssue"
        );
      };

    /* =======================================================
       ADD NEW
    ======================================================= */

    const handleAddNew =
      () => {
        sessionStorage.removeItem(
          "storeid"
        );

        sessionStorage.removeItem(
          "storeuserid"
        );

        sessionStorage.removeItem(
          "storeissueid"
        );

        navigate(
          "/inventory/StoreIssue"
        );
      };

    /* =======================================================
       CLEAR
    ======================================================= */

    const clearFilters =
      () => {
        setScrNo(
          ""
        );

        setFromDate(
          getSevenDaysAgo()
        );

        setToDate(
          getTodayDate()
        );

        setData(
          []
        );

        setSelectedItems(
          []
        );

        setCurrentPage(
          1
        );

        toast.info(
          "Filters cleared"
        );
      };

    /* =======================================================
       APPROVAL BADGE
    ======================================================= */

    const getApproveBadge =
      (approve) => {
        const approved =
          approve ===
          "Yes";

        if (approved) {
          return isDark
            ? "bg-emerald-900/30 text-emerald-300 border border-emerald-700/50"
            : "bg-emerald-50 text-emerald-700 border border-emerald-200";
        }

        return isDark
          ? "bg-amber-900/30 text-amber-300 border border-amber-700/50"
          : "bg-amber-50 text-amber-700 border border-amber-200";
      };

    /* =======================================================
       PAGINATION NUMBERS
    ======================================================= */

    const paginationPages =
      useMemo(() => {
        if (
          totalPages <=
          5
        ) {
          return Array.from(
            {
              length:
                totalPages,
            },
            (
              _,
              index
            ) =>
              index + 1
          );
        }

        if (
          currentPage <=
          3
        ) {
          return [
            1,
            2,
            3,
            4,
            5,
          ];
        }

        if (
          currentPage >=
          totalPages - 2
        ) {
          return [
            totalPages -
              4,
            totalPages -
              3,
            totalPages -
              2,
            totalPages -
              1,
            totalPages,
          ];
        }

        return [
          currentPage -
            2,
          currentPage -
            1,
          currentPage,
          currentPage +
            1,
          currentPage +
            2,
        ];
      }, [
        currentPage,
        totalPages,
      ]);

    const showingFrom =
      data.length === 0
        ? 0
        : indexOfFirstItem +
          1;

    const showingTo =
      Math.min(
        indexOfLastItem,
        data.length
      );

    /* =======================================================
       RENDER
    ======================================================= */

    return (
      <LayoutWrapper>
        {/* ===================================================
            HEADER
        =================================================== */}

        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              STORE ISSUE NOTE
              LISTING
            </span>
          }
          setSkin={
            setSkin
          }
          currentSkin={
            skin
          }
        />

        <div className="space-y-6 mt-6">
          {/* =================================================
              PAGE HEADER
          ================================================= */}

          <div
            className={
              cardClass
            }
          >
            <div
              className={`px-6 py-4 border-b flex flex-col lg:flex-row lg:items-center justify-between gap-4 ${
                isDark
                  ? "border-slate-700/50 bg-slate-900/40"
                  : "border-slate-200/50 bg-white/60"
              }`}
            >
              {/* LEFT */}

              <div className="flex items-center gap-3">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
                >
                  <FaClipboardList className="w-5 h-5 text-white" />
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3
                      className={`text-lg font-bold ${
                        isDark
                          ? "text-white"
                          : "text-slate-900"
                      }`}
                    >
                      Store Issue Notes
                    </h3>

                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        isDark
                          ? "bg-gray-700 text-gray-300"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {
                        data.length
                      }{" "}
                      Records
                    </span>

                    {selectedItems.length >
                      0 && (
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          isDark
                            ? "bg-emerald-900/30 text-emerald-300"
                            : "bg-emerald-50 text-emerald-700"
                        }`}
                      >
                        {
                          selectedItems.length
                        }{" "}
                        selected
                      </span>
                    )}

                    {(loading ||
                      deleting) && (
                      <span
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                          isDark
                            ? "bg-gray-700 text-gray-300"
                            : `bg-${baseColor}-50 text-${baseColor}-700`
                        }`}
                      >
                        <FaCog className="animate-spin" />

                        {deleting
                          ? "Deleting..."
                          : "Loading..."}
                      </span>
                    )}
                  </div>

                  <p
                    className={`text-xs mt-1 ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-600"
                    }`}
                  >
                    Search, review,
                    edit and manage
                    store issue notes
                  </p>
                </div>
              </div>

              {/* RIGHT */}

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setShowFilters(
                      (
                        previous
                      ) =>
                        !previous
                    )
                  }
                  className={
                    neutralButtonClass
                  }
                >
                  <FaFilter />

                  {showFilters
                    ? "Hide Filters"
                    : "Show Filters"}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    navigate(-1)
                  }
                  className={
                    neutralButtonClass
                  }
                >
                  <FaArrowLeft />

                  Back
                </button>

                <button
                  type="button"
                  onClick={
                    handleAddNew
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700`}
                >
                  <FaPlus />

                  Add New
                </button>
              </div>
            </div>
          </div>

          {/* =================================================
              FILTERS
          ================================================= */}

          {showFilters && (
            <div
              className={
                cardClass
              }
            >
              {/* HEADER */}

              <div
                className={`px-6 py-4 border-b ${
                  isDark
                    ? "border-slate-700/50 bg-slate-900/40"
                    : "border-slate-200/50 bg-white/60"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
                  >
                    <FaFilter className="w-4 h-4 text-white" />
                  </div>

                  <div>
                    <h3
                      className={`text-lg font-bold ${
                        isDark
                          ? "text-white"
                          : "text-slate-900"
                      }`}
                    >
                      Filtration Area
                    </h3>

                    <p
                      className={`text-xs ${
                        isDark
                          ? "text-slate-400"
                          : "text-slate-600"
                      }`}
                    >
                      Filter by date
                      range and Store
                      Issue Number
                    </p>
                  </div>
                </div>
              </div>

              {/* BODY */}

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  {/* FROM */}

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      From Date

                      <span className="text-red-500 ml-1">
                        *
                      </span>
                    </label>

                    <input
                      type="date"
                      value={
                        fromDate
                      }
                      onChange={(
                        event
                      ) =>
                        setFromDate(
                          event
                            .target
                            .value
                        )
                      }
                      className={
                        inputClass
                      }
                    />
                  </div>

                  {/* TO */}

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      To Date

                      <span className="text-red-500 ml-1">
                        *
                      </span>
                    </label>

                    <input
                      type="date"
                      value={
                        toDate
                      }
                      onChange={(
                        event
                      ) =>
                        setToDate(
                          event
                            .target
                            .value
                        )
                      }
                      className={
                        inputClass
                      }
                    />
                  </div>

                  {/* ISSUE NO */}

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      Store Issue No
                    </label>

                    <input
                      type="text"
                      value={
                        scrNo
                      }
                      onChange={(
                        event
                      ) =>
                        setScrNo(
                          event
                            .target
                            .value
                        )
                      }
                      onKeyDown={(
                        event
                      ) => {
                        if (
                          event.key ===
                          "Enter"
                        ) {
                          handleSearch();
                        }
                      }}
                      placeholder="Leave blank to search all"
                      className={
                        inputClass
                      }
                    />
                  </div>

                  {/* PAGE SIZE */}

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      Page Size
                    </label>

                    <select
                      value={
                        pageSize
                      }
                      onChange={(
                        event
                      ) => {
                        setPageSize(
                          Number(
                            event
                              .target
                              .value
                          ) ||
                            10
                        );

                        setCurrentPage(
                          1
                        );
                      }}
                      className={
                        inputClass
                      }
                    >
                      {[
                        10,
                        25,
                        50,
                        100,
                      ].map(
                        (
                          size
                        ) => (
                          <option
                            key={
                              size
                            }
                            value={
                              size
                            }
                          >
                            {
                              size
                            }
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </div>

                {/* ACTION */}

                <div
                  className={`flex flex-col md:flex-row md:items-center justify-between gap-4 mt-6 pt-5 border-t ${
                    isDark
                      ? "border-slate-700"
                      : "border-slate-200"
                  }`}
                >
                  <button
                    type="button"
                    onClick={
                      clearFilters
                    }
                    className={
                      neutralButtonClass
                    }
                  >
                    <FaTimes />

                    Clear Filters
                  </button>

                  <button
                    type="button"
                    onClick={
                      handleSearch
                    }
                    disabled={
                      loading
                    }
                    className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-60 disabled:cursor-not-allowed`}
                  >
                    {loading ? (
                      <FaCog className="animate-spin" />
                    ) : (
                      <FaSearch />
                    )}

                    {loading
                      ? "Searching..."
                      : "Search Records"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* =================================================
              TABLE CARD
          ================================================= */}

          <div
            className={
              cardClass
            }
          >
            {/* HEADER */}

            <div
              className={`p-6 border-b ${
                isDark
                  ? "border-gray-700"
                  : "border-gray-200"
              }`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                  />

                  <div>
                    <h3
                      className={`text-xl font-bold ${
                        isDark
                          ? "text-white"
                          : "text-gray-900"
                      }`}
                    >
                      Store Issue
                      Records
                    </h3>

                    <p
                      className={`text-xs mt-1 ${
                        isDark
                          ? "text-gray-400"
                          : "text-gray-500"
                      }`}
                    >
                      Select records,
                      edit existing
                      issue notes or
                      delete selected
                      rows
                    </p>
                  </div>

                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      isDark
                        ? "bg-gray-700 text-gray-300"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {
                      data.length
                    }{" "}
                    Records
                  </span>
                </div>

                {/* BULK DELETE */}

                <div className="flex flex-wrap items-center gap-3">
                  {data.length >
                    0 && (
                    <span
                      className={`text-xs font-semibold ${
                        isDark
                          ? "text-gray-400"
                          : "text-gray-600"
                      }`}
                    >
                      {
                        selectedItems.length
                      }{" "}
                      selected
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={
                      handleBulkDelete
                    }
                    disabled={
                      selectedItems.length ===
                        0 ||
                      deleting
                    }
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-red-600 hover:bg-red-700 text-white shadow transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {deleting ? (
                      <FaCog className="animate-spin" />
                    ) : (
                      <FaTrash />
                    )}

                    {deleting
                      ? "Deleting..."
                      : "Delete Selected"}

                    {selectedItems.length >
                      0 && (
                      <span className="px-2 py-0.5 rounded-full bg-white/20 text-xs">
                        {
                          selectedItems.length
                        }
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* LOADING */}

            {loading ? (
              <div className="py-16 text-center">
                <FaCog
                  className={`w-10 h-10 mx-auto animate-spin text-${baseColor}-500`}
                />

                <p
                  className={`mt-3 text-sm ${
                    isDark
                      ? "text-gray-400"
                      : "text-gray-500"
                  }`}
                >
                  Loading Store Issue
                  records...
                </p>
              </div>
            ) : data.length >
              0 ? (
              <>
                {/* TABLE */}

                <div className="overflow-x-auto">
                  <table className="min-w-[1000px] w-full text-sm">
                    <thead
                      className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                    >
                      <tr>
                        {/* SELECT ALL */}

                        <th className="px-4 py-3 text-center w-14">
                          <input
                            type="checkbox"
                            checked={
                              allCurrentSelected
                            }
                            onChange={
                              toggleAll
                            }
                            className="w-4 h-4 cursor-pointer accent-white"
                          />
                        </th>

                        <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                          S.No
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                          Store Issue No
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                          Date
                        </th>

                        <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                          Total
                        </th>

                        <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                          Approve
                        </th>

                        <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                          Action
                        </th>
                      </tr>
                    </thead>

                    <tbody
                      className={`divide-y ${
                        isDark
                          ? "divide-gray-700"
                          : "divide-gray-200"
                      }`}
                    >
                      {currentItems.map(
                        (
                          item,
                          index
                        ) => {
                          const selected =
                            selectedItems.includes(
                              item.fld_storesissueid
                            );

                          return (
                            <tr
                              key={
                                item.fld_storesissueid
                              }
                              className={`${tableRowClass} ${
                                selected
                                  ? isDark
                                    ? `bg-${baseColor}-900/20`
                                    : `bg-${baseColor}-50/70`
                                  : ""
                              }`}
                            >
                              {/* CHECKBOX */}

                              <td className="px-4 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={
                                    selected
                                  }
                                  onChange={() =>
                                    toggleOne(
                                      item.fld_storesissueid
                                    )
                                  }
                                  className={`w-5 h-5 cursor-pointer accent-${baseColor}-600`}
                                />
                              </td>

                              {/* S.NO */}

                              <td
                                className={`${tableCellClass} text-center font-medium`}
                              >
                                {indexOfFirstItem +
                                  index +
                                  1}
                              </td>

                              {/* ISSUE NO */}

                              <td className="px-4 py-3 whitespace-nowrap">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleEdit(
                                      item
                                    )
                                  }
                                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                                    isDark
                                      ? `bg-${baseColor}-900/30 text-${baseColor}-300 hover:bg-${baseColor}-900/50`
                                      : `bg-${baseColor}-50 text-${baseColor}-700 hover:bg-${baseColor}-100`
                                  }`}
                                >
                                  <FaListAlt />

                                  {item.fld_storesissueno ||
                                    "-"}
                                </button>
                              </td>

                              {/* DATE */}

                              <td
                                className={
                                  tableCellClass
                                }
                              >
                                {formatDisplayDate(
                                  item.fld_date
                                )}
                              </td>

                              {/* TOTAL */}

                              <td className="px-4 py-3 text-right whitespace-nowrap">
                                <span
                                  className={`inline-flex min-w-[100px] justify-end px-3 py-1.5 rounded-lg font-semibold ${
                                    isDark
                                      ? "bg-slate-700/60 text-slate-200"
                                      : "bg-slate-100 text-slate-800"
                                  }`}
                                >
                                  {formatAmount(
                                    item.totalNew
                                  )}
                                </span>
                              </td>

                              {/* APPROVE */}

                              <td className="px-4 py-3 text-center">
                                <span
                                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${getApproveBadge(
                                    item.approve
                                  )}`}
                                >
                                  {item.approve ===
                                  "Yes" ? (
                                    <FaCheckCircle
                                      size={
                                        11
                                      }
                                    />
                                  ) : (
                                    <FaClock
                                      size={
                                        11
                                      }
                                    />
                                  )}

                                  {item.approve ||
                                    "No"}
                                </span>
                              </td>

                              {/* EDIT */}

                              <td className="px-4 py-3 text-center">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleEdit(
                                      item
                                    )
                                  }
                                  className={`inline-flex items-center justify-center w-9 h-9 rounded-lg text-white shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700`}
                                  title="Edit Store Issue"
                                >
                                  <FaEdit
                                    size={
                                      14
                                    }
                                  />
                                </button>
                              </td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>

                {/* PAGINATION */}

                <div
                  className={`p-6 border-t ${
                    isDark
                      ? "border-gray-700"
                      : "border-gray-200"
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div
                      className={`text-sm ${
                        isDark
                          ? "text-gray-300"
                          : "text-gray-700"
                      }`}
                    >
                      Showing{" "}
                      {
                        showingFrom
                      }{" "}
                      to{" "}
                      {
                        showingTo
                      }{" "}
                      of{" "}
                      {
                        data.length
                      }{" "}
                      results
                    </div>

                    <div
                      className={`inline-flex rounded-lg shadow-sm overflow-hidden border ${
                        isDark
                          ? "border-gray-700 bg-gray-900"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {/* PREVIOUS */}

                      <button
                        type="button"
                        onClick={() =>
                          setCurrentPage(
                            (
                              previous
                            ) =>
                              Math.max(
                                1,
                                previous -
                                  1
                              )
                          )
                        }
                        disabled={
                          currentPage ===
                          1
                        }
                        className={`px-4 py-2 text-sm font-medium transition ${
                          currentPage ===
                          1
                            ? "text-gray-400 cursor-not-allowed"
                            : isDark
                            ? "text-gray-200 hover:bg-gray-800"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        Previous
                      </button>

                      {/* PAGE NUMBERS */}

                      {paginationPages.map(
                        (
                          pageNumber
                        ) => (
                          <button
                            type="button"
                            key={
                              pageNumber
                            }
                            onClick={() =>
                              setCurrentPage(
                                pageNumber
                              )
                            }
                            className={`px-4 py-2 text-sm font-medium transition ${
                              currentPage ===
                              pageNumber
                                ? `bg-${baseColor}-${baseShade} text-white`
                                : isDark
                                ? "text-gray-200 hover:bg-gray-800"
                                : "text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            {
                              pageNumber
                            }
                          </button>
                        )
                      )}

                      {/* NEXT */}

                      <button
                        type="button"
                        onClick={() =>
                          setCurrentPage(
                            (
                              previous
                            ) =>
                              Math.min(
                                totalPages,
                                previous +
                                  1
                              )
                          )
                        }
                        disabled={
                          currentPage ===
                          totalPages
                        }
                        className={`px-4 py-2 text-sm font-medium transition ${
                          currentPage ===
                          totalPages
                            ? "text-gray-400 cursor-not-allowed"
                            : isDark
                            ? "text-gray-200 hover:bg-gray-800"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* EMPTY */

              <div className="text-center py-14">
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                    isDark
                      ? "bg-gray-800"
                      : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-8 h-8 text-gray-400" />
                </div>

                <h3
                  className={`font-semibold ${
                    isDark
                      ? "text-gray-200"
                      : "text-gray-800"
                  }`}
                >
                  No Store Issue
                  Records Found
                </h3>

                <p
                  className={`text-sm mt-1 ${
                    isDark
                      ? "text-gray-400"
                      : "text-gray-500"
                  }`}
                >
                  Select the required
                  date range and click
                  Search Records.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ===================================================
            TOAST
        =================================================== */}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={
            isDark
              ? "dark"
              : "light"
          }
        />
      </LayoutWrapper>
    );
  };

export default StoreIssueNote;